import type {
  CardEffectConfig,
  CardEffectTarget,
  CardEffectTrigger,
} from "../api/respectCardsApi";

export interface SelectOption<T extends string = string> {
  value: T;
  label: string;
  helper?: string;
}

export type CardEditorPresetId =
  | "custom"
  | "aotdClaimGate"
  | "aotdHideRace"
  | "aotdNotifyOwnerOnly"
  | "aotdMuteSelf"
  | "aotdHeist"
  | "aotdShield"
  | "respectMultiplier"
  | "respectBonusAotd"
  | "commentPin"
  | "commentHighlight"
  | "commentAnonymous"
  | "economyGift"
  | "agentInfluence";

export interface CardEditorPreset {
  id: CardEditorPresetId;
  label: string;
  description: string;
  category: string;
  tags: string[];
  effect: CardEffectConfig;
}

function visibilityDefaults(
  surface: string,
  overrides?: Partial<{
    public: boolean;
    showOwner: boolean;
    showCard: boolean;
    showTimer: boolean;
    audience: "public" | "authenticated" | "owner" | "beneficiary";
  }>,
): Record<string, unknown> {
  return {
    public: true,
    showOwner: true,
    showCard: true,
    showTimer: true,
    audience: "public",
    surfaces: [surface],
    ...(overrides ?? {}),
  };
}

export const CARD_CATEGORY_OPTIONS: SelectOption[] = [
  { value: "aotd", label: "AOTD" },
  { value: "comments", label: "Comments" },
  { value: "economy", label: "Economy" },
  { value: "social", label: "Social" },
  { value: "notification", label: "Notifications" },
  { value: "agent-influence", label: "Agent influence game" },
];

export const EFFECT_TARGET_OPTIONS: SelectOption<CardEffectTarget>[] = [
  { value: "self", label: "Self" },
  { value: "target_user", label: "Target user" },
  { value: "global", label: "Global" },
  { value: "entity", label: "Entity" },
];

export const EFFECT_TRIGGER_OPTIONS: SelectOption<CardEffectTrigger>[] = [
  { value: "on_play", label: "On play" },
  { value: "on_next_action", label: "On next action" },
  { value: "passive", label: "Passive" },
];

export const VISIBILITY_AUDIENCE_OPTIONS: SelectOption[] = [
  { value: "public", label: "Public" },
  { value: "authenticated", label: "Signed-in users" },
  { value: "owner", label: "Owner only" },
  { value: "beneficiary", label: "Beneficiaries only" },
];

export const SURFACE_OPTIONS: SelectOption[] = [
  { value: "", label: "Auto from card scope/category" },
  { value: "aotd", label: "AOTD page" },
  { value: "comments", label: "Comments" },
  { value: "notifications", label: "Notifications" },
  { value: "economy", label: "Economy" },
  { value: "social", label: "Social" },
  { value: "agent-game", label: "Agent game" },
];

export const RECIPIENT_FIELD_OPTIONS: SelectOption[] = [
  { value: "", label: "Card owner / default logic" },
  { value: "$actor", label: "Event actor" },
  { value: "commentAuthorId", label: "Comment author" },
  { value: "aotdAuthorId", label: "AOTD author" },
  { value: "targetUserId", label: "Target user" },
];

export const OWNERSHIP_CHECK_OPTIONS: SelectOption[] = [
  { value: "", label: "Default ownership check" },
  { value: "commentAuthorId", label: "Comment author" },
  { value: "aotdAuthorId", label: "AOTD author" },
  { value: "targetUserId", label: "Target user" },
];

export const CONSUME_ON_OPTIONS: SelectOption[] = [
  { value: "", label: "Do not force consume" },
  { value: "first_match", label: "Consume on first match" },
  {
    value: "first_matching_author_like",
    label: "Consume on first matching author like",
  },
];

export const KNOWN_EFFECT_TYPE_OPTIONS: SelectOption[] = [
  { value: "custom", label: "Custom / advanced" },
  { value: "lock_creation_window", label: "Lock AOTD claim window" },
  { value: "hide_race_time", label: "Hide AOTD race time" },
  {
    value: "suppress_other_aotd_notifications",
    label: "Only owner gets AOTD notifications",
  },
  { value: "mute_notifications", label: "Mute owner notifications" },
  { value: "respect_multiplier", label: "Respect multiplier" },
  { value: "respect_bonus", label: "Respect bonus" },
  { value: "pin_comment", label: "Pin comment" },
  { value: "highlight_comment", label: "Highlight comment" },
  { value: "anonymize", label: "Anonymous comment" },
  { value: "transfer_respect", label: "Transfer respect" },
  { value: "early_access", label: "Early access" },
  { value: "preview_theme", label: "Preview theme" },
  { value: "protect_aotd", label: "Protect AOTD" },
  { value: "suppress_notifications", label: "Suppress notifications" },
  { value: "agent_influence", label: "Agent influence role" },
  { value: "aotd_heist_attempt", label: "Heist (steal AOTD post-claim)" },
  { value: "aotd_protect", label: "Shield (passive Heist defense)" },
];

export const KNOWN_EFFECT_SCOPE_OPTIONS: SelectOption[] = [
  { value: "aotd", label: "AOTD" },
  { value: "aotd.claim", label: "AOTD claim" },
  { value: "aotd.race", label: "AOTD race" },
  { value: "aotd.notification", label: "AOTD notifications" },
  { value: "aotd.protect", label: "AOTD protect (Shield)" },
  { value: "aotd.view", label: "AOTD view" },
  { value: "comment", label: "Comment" },
  { value: "comment.post", label: "Comment post" },
  { value: "comment.author_liked", label: "Comment author liked" },
  { value: "economy", label: "Economy" },
  { value: "notification", label: "Notification" },
  { value: "agent_influence.none", label: "Agent influence (game-only)" },
  { value: "*", label: "Any scope" },
];

export const CARD_EDITOR_PRESETS: CardEditorPreset[] = [
  {
    id: "custom",
    label: "Custom / advanced",
    description: "Leave the engine fields open and edit advanced params manually.",
    category: "aotd",
    tags: [],
    effect: {
      type: "custom",
      target: "self",
      scope: "aotd",
      trigger: "on_play",
      params: {},
    },
  },
  {
    id: "aotdClaimGate",
    label: "AOTD claim lock",
    description: "Only the player who used the card can claim AOTD during the short window.",
    category: "aotd",
    tags: ["aotd", "control"],
    effect: {
      type: "lock_creation_window",
      target: "self",
      scope: "aotd",
      trigger: "on_play",
      params: {
        durationSeconds: 30,
        visibility: visibilityDefaults("aotd"),
      },
    },
  },
  {
    id: "aotdHideRace",
    label: "Hide race time",
    description: "Hides the player's race timing while the effect is active.",
    category: "aotd",
    tags: ["aotd", "race"],
    effect: {
      type: "hide_race_time",
      target: "self",
      scope: "aotd",
      trigger: "on_play",
      params: {
        durationSeconds: 30,
        visibility: visibilityDefaults("aotd"),
      },
    },
  },
  {
    id: "aotdNotifyOwnerOnly",
    label: "Owner-only AOTD notifications",
    description: "Blocks AOTD notifications for everyone except the card owner.",
    category: "notification",
    tags: ["aotd", "notifications"],
    effect: {
      type: "suppress_other_aotd_notifications",
      target: "self",
      scope: "notification",
      trigger: "on_play",
      params: {
        durationSeconds: 30,
        visibility: visibilityDefaults("aotd"),
      },
    },
  },
  {
    id: "aotdMuteSelf",
    label: "Mute my notifications",
    description: "Silences notification delivery for the owner only.",
    category: "notification",
    tags: ["notifications", "stealth"],
    effect: {
      type: "mute_notifications",
      target: "self",
      scope: "notification",
      trigger: "on_play",
      params: {
        durationSeconds: 30,
        visibility: visibilityDefaults("notifications", {
          public: false,
          showOwner: false,
          showCard: false,
          showTimer: false,
          audience: "owner",
        }),
      },
    },
  },
  {
    id: "respectMultiplier",
    label: "Respect multiplier",
    description: "Adds bonus respect on a matching future action or passive event.",
    category: "comments",
    tags: ["respect", "bonus"],
    effect: {
      type: "respect_multiplier",
      target: "self",
      scope: "comment.author_liked",
      trigger: "on_next_action",
      params: {
        multiplier: 2,
        recipientField: "commentAuthorId",
        visibility: visibilityDefaults("comments"),
      },
    },
  },
  {
    id: "respectBonusAotd",
    label: "AOTD viewer respect bonus",
    description: "Grants a flat respect bonus to people affected by the card on AOTD view.",
    category: "aotd",
    tags: ["respect", "aotd"],
    effect: {
      type: "respect_bonus",
      target: "global",
      scope: "aotd.view",
      trigger: "passive",
      params: {
        reward: { respect: 0.1 },
        recipientField: "$actor",
        visibility: visibilityDefaults("aotd"),
      },
    },
  },
  {
    id: "commentPin",
    label: "Pin next comment",
    description: "Pins the next qualifying comment created by the card owner.",
    category: "comments",
    tags: ["comments", "boost"],
    effect: {
      type: "pin_comment",
      target: "entity",
      scope: "comment.post",
      trigger: "on_next_action",
      params: {
        pinDurationHours: 1,
        visibility: visibilityDefaults("comments"),
      },
    },
  },
  {
    id: "commentHighlight",
    label: "Highlight next comment",
    description: "Highlights the next qualifying comment created by the card owner.",
    category: "comments",
    tags: ["comments", "boost"],
    effect: {
      type: "highlight_comment",
      target: "entity",
      scope: "comment.post",
      trigger: "on_next_action",
      params: {
        highlightDurationHours: 24,
        visibility: visibilityDefaults("comments"),
      },
    },
  },
  {
    id: "commentAnonymous",
    label: "Anonymous next comment",
    description: "Makes the next qualifying comment anonymous.",
    category: "comments",
    tags: ["comments", "stealth"],
    effect: {
      type: "anonymize",
      target: "entity",
      scope: "comment.post",
      trigger: "on_next_action",
      params: {
        visibility: visibilityDefaults("comments", {
          showOwner: false,
        }),
      },
    },
  },
  {
    id: "economyGift",
    label: "Gift respect",
    description: "Transfers respect to a chosen target user immediately.",
    category: "economy",
    tags: ["economy", "gift"],
    effect: {
      type: "transfer_respect",
      target: "target_user",
      scope: "economy",
      trigger: "on_play",
      params: {
        amount: 0.1,
        taxFree: true,
        visibility: visibilityDefaults("economy", {
          public: false,
          audience: "beneficiary",
          showTimer: false,
        }),
      },
    },
  },
  {
    id: "aotdHeist",
    label: "Heist — steal today's AOTD",
    description:
      "Legendary post-claim contestation. Inside the configured window after the AOTD is claimed, regenerates today's AOTD from the stealer's staged next-AOTD emojis and flips ownership. Draft-only until the heist engine ships.",
    category: "aotd",
    tags: ["aotd", "legendary", "contestation"],
    effect: {
      type: "aotd_heist_attempt",
      target: "entity",
      scope: "aotd.claim",
      trigger: "on_play",
      params: {
        openAfterClaimSeconds: 270,
        windowSeconds: 1500,
        requireStagedEmojis: true,
        blockedMessageNoEmojis:
          "Set your next-AOTD emojis first — Heist needs a seed.",
        blockedMessageOutsideWindow:
          "The contestation window has not opened (or has closed) for today's AOTD.",
        deflectedMessage:
          "Your Heist hit a Shield — today's claim holds.",
        visibility: visibilityDefaults("aotd"),
      },
      priority: 360,
    },
  },
  {
    id: "aotdShield",
    label: "Shield — defend against Heist",
    description:
      "Passive 24h defense. While active, the first Heist played against today's AOTD author silently deflects (the attacker sees a generic 'today's claim holds' message; Shield is consumed).",
    category: "aotd",
    tags: ["aotd", "defense", "shield"],
    effect: {
      type: "aotd_protect",
      target: "self",
      scope: "aotd.protect",
      trigger: "on_play",
      params: {
        durationSeconds: 86400,
        ownerOnly: true,
        visibility: visibilityDefaults("aotd", { audience: "owner" }),
      },
      priority: 350,
    },
  },
  {
    id: "agentInfluence",
    label: "Agent influence role",
    description: "Game-only role card used as a seat in the agent influence game.",
    category: "agent-influence",
    tags: ["agent-game", "influence"],
    effect: {
      type: "agent_influence",
      target: "self",
      scope: "agent_influence.none",
      trigger: "passive",
      params: {
        visibility: visibilityDefaults("agent-game", {
          showTimer: false,
        }),
      },
    },
  },
];

export function collectStringOptions(
  values: Array<string | null | undefined>,
  defaults: string[] = [],
): string[] {
  const set = new Set<string>();
  defaults.forEach((value) => {
    const normalized = String(value || "").trim();
    if (normalized) set.add(normalized);
  });
  values.forEach((value) => {
    const normalized = String(value || "").trim();
    if (normalized) set.add(normalized);
  });
  return [...set].sort((a, b) => a.localeCompare(b));
}

export function inferCardEditorPresetId(
  effect?: CardEffectConfig | null,
  category?: string | null,
): CardEditorPresetId {
  const type = String(effect?.type || "").trim();
  if (String(category || "").trim() === "agent-influence" || type === "agent_influence") {
    return "agentInfluence";
  }
  switch (type) {
    case "lock_creation_window":
      return "aotdClaimGate";
    case "hide_race_time":
      return "aotdHideRace";
    case "suppress_other_aotd_notifications":
      return "aotdNotifyOwnerOnly";
    case "mute_notifications":
      return "aotdMuteSelf";
    case "respect_multiplier":
      return "respectMultiplier";
    case "respect_bonus":
      return "respectBonusAotd";
    case "pin_comment":
      return "commentPin";
    case "highlight_comment":
      return "commentHighlight";
    case "anonymize":
      return "commentAnonymous";
    case "transfer_respect":
      return "economyGift";
    case "aotd_heist_attempt":
      return "aotdHeist";
    case "aotd_protect":
      return "aotdShield";
    default:
      return "custom";
  }
}

export function getCardEditorPreset(id: CardEditorPresetId): CardEditorPreset {
  return CARD_EDITOR_PRESETS.find((preset) => preset.id === id) ?? CARD_EDITOR_PRESETS[0]!;
}

/**
 * Phase 2 cards-launch — single dictionary of admin tooltip copy.
 * Every form/control in the rebuilt CardsAdminPage routes through
 * `<InfoDot fieldKey="...">` which looks up its body here. Test
 * `cardEditorOptions.tooltips.test.ts` asserts every required key has
 * non-empty copy so the rebuilt admin can ship without missing
 * `(i)` content.
 */
export const CARD_EDITOR_TOOLTIPS = {
  // Top-level card fields
  "card.slug":
    "Stable identifier used in URLs, telemetry, and seed JSON. Must be unique, lower-case, alphanumeric + underscore.",
  "card.name": "Human-readable card name shown to users. 60 chars max recommended.",
  "card.description":
    "Short blurb shown in inventories and the play modal. Be concrete about what the card does.",
  "card.flavor":
    "Optional flavor text shown under the description. Pure tone — should never describe mechanics.",
  "card.rarity":
    "Drives drop weight, audit policy, and visual treatment. legendary>epic>rare>common.",
  "card.category":
    "High-level grouping (aotd, comments, economy, …). Drives default surface tags and category icons in the locked grid.",
  "card.emoji": "Single emoji used in toasts, banners, and tile chips.",
  "card.durationHours":
    "Hours the played effect lasts. Leave null for instant/one-shot. For sub-hour windows use effect.params.durationSeconds.",
  "card.droppable":
    "When false the card is hidden from the daily drop and the locked silhouette grid.",
  "card.stackable":
    "When true a user can hold more than one in_hand instance simultaneously.",
  "card.maxPerHand":
    "Soft cap on simultaneous in_hand copies. Issuance flows refuse new copies when this is reached.",
  "card.unplayedValidityDays":
    "If set, an unplayed instance auto-expires this many days after issuance. The nightly task flips status to expired.",
  "card.tags":
    "Free-form labels used for filtering and recommender heuristics.",
  "card.imageUrl": "Card art URL. Empty falls back to RespectCard's emoji-only render.",
  "card.imagePrompt":
    "Prompt used by the AI image generator. Keep style + subject focused; the harness handles seeds.",
  "card.isActive":
    "Master toggle. Inactive cards never enter daily drops, recommender output, or the locked grid.",
  // Effect block
  "effect.type":
    "Engine effect type (e.g. lock_creation_window, comment_anonymize). Must be in the impact registry — undocumented types fail the build.",
  "effect.scope":
    "Scope namespace the effect targets (aotd, comment.post, notification, …). Used for sanity checks and the redactor.",
  "effect.target":
    "Who the effect applies to: self, target_user, global, or entity (the play context entity, e.g. a comment).",
  "effect.trigger":
    "When the effect fires: on_play (immediate), on_next_action (waits for a matching event), passive (long-running).",
  "effect.priority":
    "Resolution priority when multiple effects fight for the same scope. Higher wins.",
  "effect.params":
    "Free-form bag passed to the engine. Documented per type in the Impact Map.",
  "effect.params.durationSeconds":
    "Use this for sub-hour windows (e.g. Sprint Lead's 4.5s). Takes precedence over durationHours when both are set.",
  "effect.params.startAtAnchor":
    "Anchors the duration to a known time (e.g. aotd.scheduledTime) instead of play time.",
  "effect.params.allowedUserIdsAuto":
    'Set to "owner" to auto-grant access to the card holder while the lock is active.',
  "effect.params.blockedMessage":
    "Message shown to users blocked by the effect. Keep it short and on-brand.",
  "effect.params.consumeOn":
    "When the effect should consume itself. Typically first_match for next-action effects.",
  "effect.params.recipientField":
    "Which event field identifies the recipient (e.g. commentAuthorId, $actor).",
  "effect.params.multiplier":
    "Multiplier applied to base respect on a respect_multiplier match.",
  "effect.params.reward.respect":
    "Flat respect granted per matching event for respect_bonus.",
  "effect.behaviorModule":
    "Optional reference to a compiled engine behavior module (escape hatch for non-standard logic).",
  "effect.behavior.shouldActivate":
    "Tier-2 dynamic JS body. Returns truthy to allow activation.",
  "effect.behavior.execute":
    "Tier-2 dynamic JS body. Runs when the effect resolves.",
  "effect.behavior.onPlay":
    "Tier-2 dynamic JS body. Runs when the card is played, before normal activation.",
  "effect.behavior.onExpire":
    "Tier-2 dynamic JS body. Runs when the effect expires or is consumed.",
  // Visibility
  "visibility.public":
    "When true, the active effect surfaces in public banners. Card name/emoji/slug stay redacted unless showCard is also on.",
  "visibility.showOwner":
    "Reveal the owner's username in non-owner views. Off by default for stealth-style cards.",
  "visibility.showCard":
    "Reveal the source card name + emoji in non-owner views.",
  "visibility.showTimer":
    "Reveal the active-effect countdown to non-owner viewers.",
  "visibility.audience":
    "Who can read this effect: public, authenticated, owner, or beneficiary.",
  "visibility.surfaces":
    "Surface tags driving where the SurfaceCardFeed shows the card. Empty = inferred from category.",
  // Schedule
  "schedule.baseHitChance":
    "Per-slot base probability of awarding a card. Combined with the decay curve and AOTD bonus to produce the effective chance.",
  "schedule.decayPerSlot":
    "Multiplicative decay applied as more pulls happen during the day. Higher = scarcer late drops.",
  "schedule.aotdBonus.enabled":
    "Master toggle for the AOTD-holder bonus. When off, the pct bonus is ignored even if non-zero.",
  "schedule.aotdBonus.pctBonus":
    "Percentage point bonus added to the effective hit chance for the user holding today's AOTD.",
  "schedule.aotdBonus.appliesToCurrentAotdHolder":
    "When false, the bonus is computed but never applied (useful for previews).",
  "schedule.decay.minMultiplier":
    "Floor for the decay multiplier — late pulls never drop below this fraction of the base hit chance.",
  "schedule.pinnedCardId":
    "Pin a specific card slug into a slot. Drop generation will honour it as long as it's active and droppable.",
  "schedule.slot.rarity": "Rarity tier this slot fills. Determines the eligible card pool.",
  "schedule.slot.pinnedCardId":
    "Forces this slot to a specific card slug. Falls back to the rarity pool if the slug becomes inactive.",
  "schedule.opensAt": "UTC time the daily drop opens for pulls.",
  "schedule.closesAt": "UTC time the daily drop closes. Pulls past this time are rejected.",
  "schedule.simulation.pullers":
    "How many users to simulate pulling today. Drives the expected per-slot hit/nothing distribution.",
  "schedule.simulation.aotdHolderIndex":
    "Which simulated user (0-based) holds today's AOTD. Set -1 to disable the bonus in this run.",
  "schedule.simulation.fixedUserList":
    "Optional explicit user IDs to replay deterministically. Empty = generated IDs.",
} as const;

export type CardEditorTooltipKey = keyof typeof CARD_EDITOR_TOOLTIPS;

/** Required keys — the tooltip-coverage test asserts each has non-empty copy. */
export const REQUIRED_TOOLTIP_KEYS: CardEditorTooltipKey[] = [
  "card.slug",
  "card.rarity",
  "card.category",
  "card.durationHours",
  "card.unplayedValidityDays",
  "effect.type",
  "effect.scope",
  "effect.target",
  "effect.trigger",
  "effect.priority",
  "effect.params",
  "effect.params.durationSeconds",
  "visibility.public",
  "visibility.showOwner",
  "visibility.showCard",
  "visibility.showTimer",
  "visibility.audience",
  "visibility.surfaces",
  "schedule.baseHitChance",
  "schedule.decayPerSlot",
  "schedule.decay.minMultiplier",
  "schedule.aotdBonus.enabled",
  "schedule.aotdBonus.pctBonus",
  "schedule.pinnedCardId",
  "schedule.simulation.pullers",
  "schedule.simulation.aotdHolderIndex",
];

// Phase 2b cards-launch — runtime safety net: in dev, fail loud if a required
// tooltip key is empty so the test below can never silently regress.
if (typeof process !== "undefined" && process.env && process.env.NODE_ENV === "development") {
  const missing = REQUIRED_TOOLTIP_KEYS.filter(
    (key) => !((CARD_EDITOR_TOOLTIPS as Record<string, string>)[key] || "").trim(),
  );
  if (missing.length > 0) {
    throw new Error(
      `cardEditorOptions: missing tooltip copy for required keys: ${missing.join(", ")}`,
    );
  }
}

export function slugifyCardName(name: string): string {
  const normalized = String(name || "")
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "_")
    .replace(/^_+|_+$/g, "")
    .replace(/_{2,}/g, "_");
  if (!normalized) {
    return "";
  }
  return /^[a-z]/.test(normalized) ? normalized : `card_${normalized}`;
}
